
import React, { useState, useEffect, useCallback } from 'react';
import { ExtractedData, AsanaTask, TaskTemplate } from '../types';
import { getWorkspaces, createProject, createTasks, uploadAttachment, fetchTemplates, generateTasksFromTemplates } from '../services/asanaService';
import { AlertCircle, Loader2, Play, LayoutList, ExternalLink, RefreshCw, Paperclip, Cloud } from 'lucide-react';

interface AsanaCreatorProps {
  data: ExtractedData;
}

export const AsanaCreator: React.FC<AsanaCreatorProps> = ({ data }) => {
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [status, setStatus] = useState<'IDLE' | 'FETCHING_WS' | 'CREATING' | 'SUCCESS' | 'ERROR'>('IDLE');
  const [logs, setLogs] = useState<string[]>([]);
  const [tasks, setTasks] = useState<AsanaTask[]>([]);
  const [projectUrl, setProjectUrl] = useState<string | null>(null);

  const addLog = (msg: string) => setLogs(prev => [...prev, msg]);

  const refreshTasks = useCallback(async () => {
    // We always use the 'data' prop which is passed from App.tsx
    // If user edited data in Review step, 'data' here will be the updated version.
    const templates = await fetchTemplates();
    const processed = generateTasksFromTemplates(data, templates);
    setTasks(processed);
  }, [data]);

  const handleFetchWorkspaces = useCallback(async () => {
    setStatus('FETCHING_WS');
    try {
      const res = await getWorkspaces();
      setWorkspaces(res.data);
      if (res.data.length > 0) setSelectedWorkspace(res.data[0].gid);
      setStatus('IDLE');
    } catch (e: any) {
      addLog(`Error fetching workspaces: ${e.message}`);
      setStatus('ERROR');
    }
  }, []);

  useEffect(() => {
    refreshTasks();
    
    // Only fetch workspaces if not already loaded (though re-fetching is fine)
    if (workspaces.length === 0) {
        handleFetchWorkspaces();
    }
    
    // Listen for template changes, but also react to 'data' prop changes
    const handleTemplateUpdate = () => refreshTasks();
    window.addEventListener('myesalon:templates-updated', handleTemplateUpdate);
    return () => window.removeEventListener('myesalon:templates-updated', handleTemplateUpdate);
  }, [data, handleFetchWorkspaces, refreshTasks, workspaces.length]);

  const handleExecute = async () => {
    if (!selectedWorkspace) {
        alert("Please select an Asana workspace first.");
        return;
    }
    
    // Final check for the most up-to-date content before running
    const currentTemplates = await fetchTemplates();
    const finalTasks = generateTasksFromTemplates(data, currentTemplates);
    setTasks(finalTasks);

    setStatus('CREATING');
    setLogs([]);
    setProjectUrl(null);
    addLog("Pulling latest cloud templates...");

    try {
      addLog(`Creating project "${data.businessInfo.name} (${data.businessInfo.city}, ${data.businessInfo.state})"...`);
      const projectRes = await createProject(selectedWorkspace, data);
      const projectId = projectRes.data.gid;
      setProjectUrl(projectRes.data.permalink_url);

      addLog(`Creating ${finalTasks.length} tasks...`);
      const createdTasks = await createTasks(projectId, finalTasks);

      const invoiceTask = createdTasks.find(t => t.name.toLowerCase().includes('invoice'));
      if (invoiceTask && data.pdfData && data.pdfData.length > 0) {
        addLog(`Attaching PDF source files...`);
        for (let i = 0; i < data.pdfData.length; i++) {
          const fileName = `Source_${i + 1}_${data.businessInfo.name.replace(/\s+/g, '_')}.pdf`;
          await uploadAttachment(invoiceTask.gid, data.pdfData[i], fileName);
        }
      }

      addLog("Automation complete!");
      setStatus('SUCCESS');
    } catch (e: any) {
      addLog(`Failed: ${e.message}`);
      setStatus('ERROR');
    }
  };

  return (
    <div className="glass-panel rounded-3xl shadow-xl border-white/50 overflow-hidden">
      <div className="p-6 border-b border-white/30 bg-gradient-to-r from-red-50/50 to-transparent">
        <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2.5 rounded-xl shadow-sm">
                    <LayoutList className="w-6 h-6 text-red-600" />
                </div>
                <div>
                    <h3 className="text-xl font-bold text-slate-800">1. Asana Automation</h3>
                    <p className="text-sm text-slate-500 truncate max-w-[200px]">Cloud-synced engine for {data.businessInfo.name}</p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <Cloud className="w-4 h-4 text-blue-400" />
                <button onClick={refreshTasks} className="p-2 text-slate-400 hover:text-blue-600 transition-colors" title="Refresh Previews"><RefreshCw className="w-5 h-5" /></button>
            </div>
        </div>
      </div>
      
      <div className="p-6 space-y-6">
        {workspaces.length > 0 ? (
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1 ml-1">Select Asana Workspace</label>
            <select 
              value={selectedWorkspace} 
              onChange={(e) => setSelectedWorkspace(e.target.value)}
              className="glass-input w-full p-3 rounded-xl outline-none text-slate-700 font-medium cursor-pointer"
            >
              {workspaces.map((ws: any) => (
                <option key={ws.gid} value={ws.gid}>{ws.name}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="space-y-4">
            {status === 'FETCHING_WS' ? (
                <div className="flex items-center gap-2 text-slate-400 italic text-sm"><Loader2 className="animate-spin w-4 h-4" /> Loading workspaces...</div>
            ) : status === 'ERROR' && (
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl flex gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <p className="text-xs text-amber-800"><b>Could not load Asana workspaces.</b> Ensure <code>ASANA_PAT</code> is configured on the server and try refreshing.</p>
                </div>
            )}
          </div>
        )}

        <div className="bg-white/40 rounded-xl p-4 border border-white/50 backdrop-blur-sm shadow-inner">
            <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-[10px] text-slate-400 uppercase tracking-widest">Preview Tasks</h4>
                <span className="text-[10px] font-bold text-blue-500 flex items-center gap-1"><RefreshCw className="w-2.5 h-2.5" /> Updated Live</span>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1 custom-scrollbar">
                {tasks.map((task, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm bg-white/70 p-3 rounded-xl border border-white/60 shadow-sm group hover:bg-white/90 transition-colors">
                        <span className="bg-slate-100 text-slate-400 text-[10px] px-1.5 py-0.5 rounded font-mono font-bold group-hover:bg-blue-100 group-hover:text-blue-600 transition-colors">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-700 leading-tight flex items-center gap-2">
                                {task.name}
                                {task.name.toLowerCase().includes('invoice') && <Paperclip className="w-3 h-3 text-blue-500" />}
                            </p>
                            <p className="text-slate-500 text-[11px] mt-1 whitespace-pre-wrap leading-relaxed truncate-3-lines">{task.notes}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>

        <div className="space-y-4">
            {status === 'SUCCESS' && projectUrl ? (
                <a href={projectUrl} target="_blank" className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl flex items-center justify-center gap-2 font-bold text-lg shadow-lg shadow-emerald-200 transition-all hover:scale-[1.02]">
                    <ExternalLink className="w-5 h-5" /> Open Asana Project
                </a>
            ) : (
                <button
                    onClick={handleExecute}
                    disabled={!selectedWorkspace || status === 'CREATING'}
                    className={`w-full py-3.5 rounded-xl flex items-center justify-center gap-2 font-bold text-white transition-all shadow-lg ${status === 'SUCCESS' ? 'bg-green-600' : 'bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 shadow-red-200'} ${(!selectedWorkspace || status === 'CREATING') ? 'opacity-50' : 'active:scale-95'}`}
                >
                    {status === 'CREATING' ? <><Loader2 className="w-5 h-5 animate-spin" /><span>Syncing & Building...</span></> : <><Play className="w-5 h-5" /> Run Automation</>}
                </button>
            )}

            {logs.length > 0 && (
                <div className="bg-slate-900/90 text-green-400 p-4 rounded-xl font-mono text-[10px] max-h-32 overflow-y-auto backdrop-blur-md border border-slate-700 shadow-inner">
                    {logs.map((log, i) => <div key={i} className="mb-0.5"><span className="opacity-50 mr-2">[{new Date().toLocaleTimeString()}]</span>{log}</div>)}
                </div>
            )}
        </div>
      </div>
    </div>
  );
};
